import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  ClipboardList,
  TrendingUp,
  Target,
  Apple,
  Flame,
  User,
  Star,
  Send,
  ChevronRight,
  MessageSquare,
  Eye,
  Search,
} from "lucide-react";
import { API } from "@/App";
import { formatDateLongTR } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

// Category icons
const CATEGORY_ICONS = {
  general: ClipboardList,
  progress: TrendingUp,
  technique: Target,
  nutrition: Apple,
  motivation: Flame,
};

const CATEGORY_COLORS = {
  blue: "bg-blue-100 text-blue-700",
  green: "bg-green-100 text-green-700",
  purple: "bg-purple-100 text-purple-700",
  orange: "bg-orange-100 text-orange-700",
  red: "bg-red-100 text-red-700",
};

export default function EvaluationsModule({ businessId, user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "evaluations"]);

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberEvaluations, setMemberEvaluations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // Modals
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Form
  const [editingEval, setEditingEval] = useState(null);
  const [form, setForm] = useState({
    title: "",
    content: "",
    category: "general",
    rating: null,
    is_private: false,
  });

  useEffect(() => {
    if (businessId) {
      fetchMembers();
      fetchCategories();
    }
  }, [businessId]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/evaluations/trainer/members?business_id=${businessId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/evaluations/categories`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchMemberEvaluations = async (memberId) => {
    try {
      const response = await fetch(
        `${API}/evaluations/trainer/member/${memberId}?business_id=${businessId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setMemberEvaluations(data);
      }
    } catch (error) {
      console.error("Error fetching evaluations:", error);
    }
  };

  const handleSelectMember = async (member) => {
    setSelectedMember(member);
    await fetchMemberEvaluations(member.user_id);
    setShowMemberModal(true);
  };

  const handleCreateEvaluation = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      toast.error(t("common:toast_baslikGerekli"));
      return;
    }
    if (!form.content.trim()) {
      toast.error(t("common:toast_i̇cerikGerekli"));
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      const url = editingEval
        ? `${API}/evaluations/trainer/${editingEval.evaluation_id}`
        : `${API}/evaluations/trainer?business_id=${businessId}`;

      const body = editingEval
        ? form
        : { ...form, member_id: selectedMember.user_id };

      const response = await fetch(url, {
        method: editingEval ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success(editingEval ? t("evaluations:evaluationUpdated") : t("evaluations:evaluationCreated"));
        await fetchMemberEvaluations(selectedMember.user_id);
        fetchMembers(); // Refresh counts
        handleCloseCreateModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteEvaluation = async (evalId) => {
    if (!confirm(t("evaluations:confirmDeleteEvaluation"))) return;

    try {
      const response = await fetch(`${API}/evaluations/trainer/${evalId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("evaluations:toast_degerlendirmeSilindi"));
        await fetchMemberEvaluations(selectedMember.user_id);
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Silinemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleOpenCreateModal = (evaluation = null) => {
    if (evaluation) {
      setEditingEval(evaluation);
      setForm({
        title: evaluation.title,
        content: evaluation.content,
        category: evaluation.category,
        rating: evaluation.rating,
        is_private: evaluation.is_private,
      });
    } else {
      setEditingEval(null);
      setForm({
        title: "",
        content: "",
        category: "general",
        rating: null,
        is_private: false,
      });
    }
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setEditingEval(null);
    setForm({
      title: "",
      content: "",
      category: "general",
      rating: null,
      is_private: false,
    });
  };

  const getIconComponent = (category) => {
    return CATEGORY_ICONS[category] || ClipboardList;
  };

  const getColorClass = (color) => {
    return CATEGORY_COLORS[color] || CATEGORY_COLORS.blue;
  };

  const formatDate = (dateStr) => {
    return formatDateLongTR(dateStr);
  };

  const filteredMembers = members.filter((m) => {
    const fullName = `${m.name} ${m.surname}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy">{t("evaluations:memberEvaluations")}</h1>
        <p className="text-grey text-sm">
          Üyelerinize geri bildirim ve değerlendirme yazın
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('members:searchMember')}
          className="pl-10 rounded-xl"
        />
      </div>

      {/* Members List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5" />
            Üyeler ({filteredMembers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredMembers.length === 0 ? (
            <div className="text-center py-8 text-grey">
              <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t("common:memberNotFound")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMembers.map((member) => (
                <div
                  key={member.user_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => handleSelectMember(member)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center font-medium">
                      {member.name?.charAt(0)}{member.surname?.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-navy">
                          {member.name} {member.surname}
                        </p>
                        {member.is_app_registered === false && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-orange-600 border-orange-300 bg-orange-50">
                            Kayıt Olmayan
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-grey">{member.email || member.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {member.evaluation_count > 0 && (
                      <Badge className="bg-blue-100 text-blue-700">
                        {member.evaluation_count} değerlendirme
                      </Badge>
                    )}
                    <ChevronRight className="w-5 h-5 text-grey" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Member Detail Modal */}
      <Dialog open={showMemberModal} onOpenChange={setShowMemberModal}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedMember && (
                <>
                  <div className="w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center font-medium">
                    {selectedMember.name?.charAt(0)}{selectedMember.surname?.charAt(0)}
                  </div>
                  <div>
                    <p>{selectedMember.name} {selectedMember.surname}</p>
                    <p className="text-sm text-grey font-normal">{selectedMember.email}</p>
                  </div>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Add Evaluation Button */}
            <Button
              onClick={() => handleOpenCreateModal()}
              className="w-full bg-navy hover:bg-navy/90 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Değerlendirme Yaz
            </Button>

            {/* Evaluations List */}
            {memberEvaluations.length === 0 ? (
              <div className="text-center py-8 text-grey">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{t("evaluations:noEvaluationsYet")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {memberEvaluations.map((evaluation) => {
                  const IconComp = getIconComponent(evaluation.category);
                  return (
                    <Card key={evaluation.evaluation_id} className="border shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`p-2 rounded-lg ${getColorClass(evaluation.category_color)}`}>
                              <IconComp className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium text-navy">{evaluation.title}</h4>
                                <Badge className={`${getColorClass(evaluation.category_color)} text-xs`}>
                                  {evaluation.category_name}
                                </Badge>
                                {evaluation.is_private && (
                                  <Badge variant="secondary" className="text-xs">{t("common:private")}</Badge>
                                )}
                              </div>
                              <p className="text-sm text-grey mt-1 whitespace-pre-wrap">
                                {evaluation.content}
                              </p>
                              {evaluation.rating && (
                                <div className="flex items-center gap-1 mt-2">
                                  {[1, 2, 3, 4, 5].map((i) => (
                                    <Star
                                      key={i}
                                      className={`w-4 h-4 ${
                                        i <= evaluation.rating
                                          ? "text-amber-500 fill-amber-500"
                                          : "text-slate-300"
                                      }`}
                                    />
                                  ))}
                                </div>
                              )}
                              <p className="text-xs text-grey mt-2">
                                {formatDate(evaluation.created_at)}
                                {evaluation.trainer_name && ` • ${evaluation.trainer_name}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenCreateModal(evaluation)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteEvaluation(evaluation.evaluation_id)}
                              className="h-8 w-8 p-0 text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Evaluation Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEval ? t('common:editEvaluation') : t('common:newEvaluation')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEvaluation} className="space-y-4">
            <div>
              <Label>Başlık *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={t("evaluations:ph_ornHaftalikİlerlemeDegerlendirmesi")}
                className="rounded-xl mt-1"
              />
            </div>

            <div>
              <Label>Kategori</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category} value={cat.category}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>İçerik *</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder={t("evaluations:ph_degerlendirmeniziYazin")}
                className="rounded-xl mt-1"
                rows={5}
              />
            </div>

            <div>
              <Label className="block mb-2">Puan (Opsiyonel)</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setForm({ ...form, rating: form.rating === rating ? null : rating })}
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                      form.rating && rating <= form.rating
                        ? "bg-amber-500 text-white"
                        : "bg-slate-100 hover:bg-slate-200"
                    }`}
                  >
                    <Star className={`w-5 h-5 ${form.rating && rating <= form.rating ? "fill-current" : ""}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Özel Değerlendirme - şimdilik gizli, diğer işletmeler zaten göremiyor
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-navy text-sm">{t("evaluations:ozelDegerlendirme")}</p>
                <p className="text-xs text-grey">{t("common:sadeceUyeGorebilir")}</p>
              </div>
              <Switch
                checked={form.is_private}
                onCheckedChange={(c) => setForm({ ...form, is_private: c })}
              />
            </div>
            */}

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseCreateModal}
                className="flex-1 rounded-xl"
                disabled={isSaving}
              >
                İptal
              </Button>
              <Button type="submit" className="flex-1 bg-navy hover:bg-navy/90 rounded-xl" disabled={isSaving}>
                <Send className="w-4 h-4 mr-2" />
                {isSaving ? "Kaydediliyor..." : (editingEval ? t('settings:update') : t('common:send'))}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
